import { useCallback } from 'react';
import { useDomainsService } from 'domain/shared/domain';
import { type FilterBarFiltersProps } from 'framework/ViewTypes/components';
import { FilterCondition } from 'framework/types';
import type { TaskConstructorFilters } from '../types';

interface UseTaskConstructorFilterBarParams {
  filters: TaskConstructorFilters;
  setFilters: (filters: TaskConstructorFilters) => void;
  resetFilters: () => void;
}

interface DomainOption {
  id: string | number;
  name: string;
}

const mapDomainOption = (domain: DomainOption) => ({ value: String(domain.id), label: domain.name });

// TODO: Replace mock object options with task constructor filters from the backend.
const objectFilters: FilterBarFiltersProps['filters'] = [
  {
    id: 'object',
    source: 'object',
    condition: FilterCondition.EQ,
    type: 'dropdown',
    label: 'Object',
    options: [{ value: 'application', label: 'Application' }],
  },
];

const useTaskConstructorFilterBar = ({
  filters: value,
  setFilters,
  resetFilters,
}: UseTaskConstructorFilterBarParams): FilterBarFiltersProps => {
  const { findAllDomains } = useDomainsService();

  const loadDomainOptions = useCallback(
    async (search: string) => {
      const response = await findAllDomains({
        filter: search ? { name: { ct: search } } : {},
      });
      return (response.result ?? []).map(mapDomainOption);
    },
    [findAllDomains],
  );

  const resolveDomainOption = useCallback(
    async (id: string) => {
      const response = await findAllDomains({ filter: { id: { eq: id } } });
      const domain: DomainOption | undefined = response.result?.[0];
      return domain ? mapDomainOption(domain) : { value: id, label: id };
    },
    [findAllDomains],
  );

  return {
    value,
    onChange: nextFilters => setFilters(nextFilters as TaskConstructorFilters),
    onReset: resetFilters,
    filters: [
      {
        id: 'domain',
        source: 'domain',
        condition: FilterCondition.EQ,
        type: 'autosuggestion',
        label: 'Domain',
        loadOptions: loadDomainOptions,
        resolveOption: resolveDomainOption,
      },
      ...objectFilters,
    ],
  };
};

export default useTaskConstructorFilterBar;
